// Dates and times as staff read them. The API speaks venue-local YYYY-MM-DD and HH:MM; the
// panel keeps those strings and only formats for display.

const DAY_MS = 86_400_000

export function todayKey(timeZone?: string): string {
  const now = new Date()
  if (!timeZone) return localKey(now)
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now)
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? ''
  return `${get('year')}-${get('month')}-${get('day')}`
}

function localKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function addDays(key: string, n: number): string {
  const [y, m, d] = key.split('-').map(Number) as [number, number, number]
  return new Date(Date.UTC(y, m - 1, d) + n * DAY_MS).toISOString().slice(0, 10)
}

export function weekdayOf(key: string): number {
  const [y, m, d] = key.split('-').map(Number) as [number, number, number]
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay()
}

/** "Sat 14 Jun" — short enough for a header on a phone. */
export function shortDate(key: string, lang = navigator.language): string {
  const [y, m, d] = key.split('-').map(Number) as [number, number, number]
  return new Intl.DateTimeFormat(lang, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, d)))
}

export function longDate(key: string, lang = navigator.language): string {
  const [y, m, d] = key.split('-').map(Number) as [number, number, number]
  return new Intl.DateTimeFormat(lang, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, d)))
}

export function relativeDay(key: string, timeZone?: string): string | null {
  const today = todayKey(timeZone)
  if (key === today) return 'Today'
  if (key === addDays(today, 1)) return 'Tomorrow'
  if (key === addDays(today, -1)) return 'Yesterday'
  return null
}

export function dateTime(iso: string, timeZone?: string, lang = navigator.language): string {
  return new Intl.DateTimeFormat(lang, { timeZone, day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(iso))
}

export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export function daysLabel(days: number[]): string {
  const sorted = [...days].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7)) // Monday first
  if (sorted.length === 7) return 'Every day'
  // Collapse runs: Mon–Fri
  const order = sorted.map((d) => (d + 6) % 7)
  const runs: string[] = []
  let start = 0
  for (let i = 1; i <= order.length; i++) {
    if (i === order.length || order[i] !== order[i - 1]! + 1) {
      const a = WEEKDAYS[sorted[start]!]!
      const b = WEEKDAYS[sorted[i - 1]!]!
      runs.push(i - 1 - start >= 2 ? `${a}–${b}` : i - 1 - start === 1 ? `${a}, ${b}` : a)
      start = i
    }
  }
  return runs.join(', ')
}

export function pluralGuests(n: number): string {
  return `${n} ${n === 1 ? 'guest' : 'guests'}`
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('')
}
